import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const RecentlyViewedContext = createContext();

const MAX_RECENT_ITEMS = 8; // Maximum number of recently viewed items to track

export const RecentlyViewedProvider = ({ children }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Load recently viewed from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('recentlyViewed');
      if (stored) {
        setRecentlyViewed(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load recently viewed:', error);
    }
  }, []);

  // Add a product to recently viewed
  const addRecentlyViewed = useCallback((product) => {
    if (!product || !product.id) return;

    setRecentlyViewed((prev) => {
      // Remove if already exists
      const filtered = prev.filter(item => item.id !== product.id);

      // Add to beginning and limit to MAX_RECENT_ITEMS
      const updated = [product, ...filtered].slice(0, MAX_RECENT_ITEMS);

      // Save to localStorage
      try {
        localStorage.setItem('recentlyViewed', JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save recently viewed:', error);
      }

      return updated;
    });
  }, []);

  // Clear all recently viewed
  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
    try {
      localStorage.removeItem('recentlyViewed');
    } catch (error) {
      console.error('Failed to clear recently viewed:', error);
    }
  }, []);

  // Get recently viewed excluding current product
  const getRecentlyViewedExcluding = useCallback((productId) => {
    return recentlyViewed.filter(item => item.id !== productId);
  }, [recentlyViewed]);

  return (
    <RecentlyViewedContext.Provider
      value={{
        recentlyViewed,
        addRecentlyViewed,
        clearRecentlyViewed,
        getRecentlyViewedExcluding
      }}
    >
      {children}
    </RecentlyViewedContext.Provider>
  );
};

export const useRecentlyViewed = () => {
  const context = useContext(RecentlyViewedContext);
  if (!context) {
    throw new Error('useRecentlyViewed must be used within RecentlyViewedProvider');
  }
  return context;
};
